import React, { Fragment, useContext, useEffect, useState, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import Box from "@mui/material/Box";
import Paper from "@mui/material/Paper";
import Stack from "@mui/material/Stack";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import TextField from "@mui/material/TextField";
import Autocomplete from "@mui/material/Autocomplete";
import InputAdornment from "@mui/material/InputAdornment";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";

import SearchIcon from "@mui/icons-material/Search";
import CloseIcon from "@mui/icons-material/Close";
import CheckIcon from "@mui/icons-material/Check";
import AddIcon from "@mui/icons-material/Add";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import PictureAsPdfOutlinedIcon from "@mui/icons-material/PictureAsPdfOutlined";
import SwapVertOutlinedIcon from "@mui/icons-material/SwapVertOutlined";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Toast } from "primereact/toast";
import "../../../../assets/css/DataTableDemo.css";
import "primereact/resources/themes/saga-blue/theme.css";
import "primereact/resources/primereact.min.css";
import "primeicons/primeicons.css";
import "../../../../assets/css/PrimeGrid.css";

import { saveAs } from "file-saver";

import { AlertContext } from "../../../../context/AlertContext";
import { AuthContext } from "../../../../context/AuthContext";
import { ConfiguracionContext } from "../../../../context/ConfiguracionContext";
import { getBrand, createBrandTheme } from "../../../../theme/brand";
import Spinner from "../../Spinner";
import Barra from "../../Barra";

const tipos = [
  { id: "E", nombre: "ENTRADA" },
  { id: "S", nombre: "SALIDA" },
];

const exportColumns = [
  { title: "ID", dataKey: "id" },
  { title: "SKU", dataKey: "sku" },
  { title: "Fecha", dataKey: "fecha" },
  { title: "Cantidad", dataKey: "cantidad" },
  { title: "Almacen", dataKey: "almacen" },
  { title: "Tipo", dataKey: "tipo" },
  { title: "Descripción", dataKey: "descripcion" },
];

export default function MovimientosMedicamentos() {
  const [globalFilter, setGlobalFilter] = useState(null);
  const [selectedRow, setSelectedRow] = useState(null);
  const [mov, guardarMov] = useState(null);
  const [stockTotal, guardarStockTotal] = useState(null);
  const [visible, setVisible] = useState(false);
  const toast = useRef(null);
  const navigate = useNavigate();

  const { mensajeAlerta } = useContext(AlertContext);
  const { state } = useContext(AuthContext);
  const {
    statusAcceso,
    almacenes,
    almacen,
    configuracion,
    medicamentos,
    movimientosMedicamentos,
    stocksMedicamentos,
    obtenerMovimientosMedicamentos,
    obtenerAcceso,
    obtenerAlmacenes,
    guardarAlmacen,
    guardarMovimientosMedicamentos,
    agregarMovimientoMedicamento,
    obtenerMedicamentosSku,
    obtenerStocksMedicamentos,
  } = useContext(ConfiguracionContext);

  const theme = createBrandTheme(getBrand(configuracion));

  useEffect(() => {
    if (state.usuario) {
      obtenerAcceso({
        usuarioId: state.usuario.id,
        layout: "MovimientosMedicamentos",
      });
    }
    obtenerMedicamentosSku({ todos: true });
    obtenerAlmacenes();
    obtenerStocksMedicamentos({ almacen: 1 });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.id]);

  useEffect(() => {
    if (statusAcceso === 400) {
      mensajeAlerta(
        "Accesos",
        "No tiene permiso para acceder a esta pantalla !.",
        "error"
      );
      navigate("/");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusAcceso]);

  const addClick = () => {
    setSelectedRow(null);
    guardarMov({ descripcion: null });
    guardarStockTotal(null);
    setVisible(true);
  };

  const onHide = () => setVisible(false);

  const aceptarClick = () => {
    if (!mov || !mov.sku || String(mov.sku).trim() === "") {
      mensajeAlerta("Movimientos de Medicamentos", "El medicamento es necesario !.", "error");
      return;
    }
    if (!mov.fecha || String(mov.fecha).trim() === "") {
      mensajeAlerta("Movimientos de Medicamentos", "La fecha es necesaria !.", "error");
      return;
    }
    if (!mov.cantidad || Number(mov.cantidad) <= 0) {
      mensajeAlerta("Movimientos de Medicamentos", "La cantidad es necesaria !.", "error");
      return;
    }
    if (!mov.tipo || String(mov.tipo).trim() === "") {
      mensajeAlerta("Movimientos de Medicamentos", "El tipo de movimiento es necesario !.", "error");
      return;
    }
    if (mov.tipo === "S" && Number(mov.cantidad) > Number(stockTotal)) {
      mensajeAlerta(
        "Movimientos de Medicamentos",
        `La cantidad ha superado el stock de farmacia (${stockTotal}) !.`,
        "error"
      );
      return;
    }

    agregarMovimientoMedicamento({ movimientoMedicamento: mov });
    onHide();
    setSelectedRow(null);
    setTimeout(() => {
      if (almacen) obtenerMovimientosMedicamentos({ almacen });
    }, 2000);
  };

  const exitClick = () => navigate("/");

  const exportPdf = () => {
    import("jspdf").then((jsPDFmod) => {
      import("jspdf-autotable").then(() => {
        const JsPDF = jsPDFmod.jsPDF || jsPDFmod.default;
        const doc = new JsPDF({ orientation: "landscape" });
        doc.setFontSize(12);
        doc.text("Listado de Movimientos de Medicamentos", 14, 10);
        doc.autoTable({
          head: [exportColumns.map((c) => c.title)],
          body: (movimientosMedicamentos || []).map((r) =>
            exportColumns.map((c) => r[c.dataKey])
          ),
          startY: 16,
          styles: { overflow: "linebreak" },
        });
        window.open(doc.output("bloburl"));
      });
    });
  };

  const exportExcel = () => {
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(movimientosMedicamentos || []);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, { bookType: "xlsx", type: "array" });
      const blob = new Blob([excelBuffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8",
      });
      saveAs(blob, `movimientosdemedicamentos_export_${new Date().getTime()}.xlsx`);
    });
  };

  const listo = medicamentos && almacenes && movimientosMedicamentos;
  const cargando = statusAcceso === 0 || statusAcceso === 400;

  // valor del Autocomplete "Almacen" del diálogo
  const almacenValue = (() => {
    if (!mov || !almacenes) return null;
    if (mov.tipo === "E") return almacenes[0] || null;
    if (mov.tipo === "S")
      return almacenes.find((a) => a.id === mov.almacen) || null;
    return null;
  })();

  return (
    <Fragment>
      <Barra />
      <Toast ref={toast} />

      {cargando ? <Spinner /> : null}

      {statusAcceso === 200 ? (
        <ThemeProvider theme={theme}>
          <Box id="dataTableBotones" sx={{ px: { xs: 1, sm: 2 }, pb: 3 }}>
            {listo ? (
              <Fragment>
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    mb: 2,
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                  }}
                >
                  <Stack
                    direction={{ xs: "column", md: "row" }}
                    spacing={2}
                    alignItems={{ xs: "stretch", md: "center" }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center" sx={{ flexShrink: 0 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          display: "grid",
                          placeItems: "center",
                          bgcolor: "primary.main",
                          color: "primary.contrastText",
                        }}
                      >
                        <SwapVertOutlinedIcon />
                      </Box>
                      <Box>
                        <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                          Movimientos de Medicamentos
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {`${movimientosMedicamentos.length} registros`}
                        </Typography>
                      </Box>
                    </Stack>

                    <Autocomplete
                      options={almacenes}
                      disablePortal
                      getOptionLabel={(o) => (o && o.nombre) || ""}
                      onChange={(e, value) => {
                        if (value) {
                          obtenerMovimientosMedicamentos({ almacen: value.id });
                          guardarAlmacen(value.id);
                        } else {
                          guardarMovimientosMedicamentos([]);
                          guardarAlmacen(null);
                        }
                      }}
                      sx={{ minWidth: { xs: "100%", md: 260 } }}
                      renderInput={(params) => (
                        <TextField {...params} label="Almacén" size="small" />
                      )}
                    />

                    <TextField
                      size="small"
                      placeholder="Buscar…"
                      onInput={(e) => setGlobalFilter(e.target.value)}
                      sx={{ minWidth: { xs: "100%", md: 200 } }}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <SearchIcon fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </Stack>

                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 2 }}>
                    <Button variant="contained" startIcon={<AddIcon />} onClick={addClick}>
                      Nuevo
                    </Button>
                    <Button
                      variant="outlined"
                      color="success"
                      startIcon={<FileDownloadOutlinedIcon />}
                      onClick={exportExcel}
                    >
                      Excel
                    </Button>
                    <Button
                      variant="outlined"
                      color="error"
                      startIcon={<PictureAsPdfOutlinedIcon />}
                      onClick={exportPdf}
                    >
                      PDF
                    </Button>
                    <Box sx={{ flexGrow: 1 }} />
                    <Button
                      variant="text"
                      color="inherit"
                      startIcon={<CloseIcon />}
                      onClick={exitClick}
                    >
                      Cerrar
                    </Button>
                  </Stack>
                </Paper>

                <Paper
                  elevation={0}
                  sx={{
                    border: "1px solid",
                    borderColor: "divider",
                    borderRadius: 2,
                    overflow: "hidden",
                    "& .p-datatable-wrapper": { overflowX: "auto" },
                    "& .p-datatable-thead > tr > th, & .p-datatable-tbody > tr > td": {
                      whiteSpace: "nowrap",
                    },
                  }}
                >
                  <DataTable
                    value={movimientosMedicamentos}
                    selection={selectedRow}
                    onSelectionChange={(e) => setSelectedRow(e.value)}
                    selectionMode="single"
                    dataKey="id"
                    className="p-datatable-sm p-datatable-striped p-datatable-responsive-demo mito-grid"
                    globalFilter={globalFilter}
                    emptyMessage="Elegí un almacén para ver los movimientos."
                    paginator
                    paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
                    currentPageReportTemplate="Mostrando {first} a {last} de {totalRecords}"
                    rows={10}
                    rowsPerPageOptions={[10, 20, 50]}
                  >
                    <Column field="sku" header="SKU" sortable />
                    <Column field="nombre" header="Nombre" sortable />
                    <Column field="fecha" header="Fecha" sortable />
                    <Column field="almacen" header="Almacén" sortable />
                    <Column field="cantidad" header="Cantidad" sortable />
                    <Column field="tipo" header="Tipo" sortable />
                    <Column field="descripcion" header="Descripción" sortable />
                  </DataTable>
                </Paper>
              </Fragment>
            ) : (
              <Spinner />
            )}
          </Box>

          <Dialog open={visible} onClose={onHide} maxWidth="sm" fullWidth>
            <DialogTitle sx={{ display: "flex", alignItems: "center", gap: 1, fontWeight: 700 }}>
              <SwapVertOutlinedIcon color="primary" />
              Nuevo movimiento
            </DialogTitle>
            <DialogContent dividers>
              <Grid container spacing={2} sx={{ mt: 0 }}>
                <Grid item xs={12}>
                  <Autocomplete
                    options={medicamentos || []}
                    disablePortal
                    getOptionLabel={(o) => (o && o.descripcion) || ""}
                    onChange={(e, value) => {
                      if (value) {
                        guardarMov({ ...mov, sku: value.sku });
                        const s = (stocksMedicamentos || []).find(
                          (x) => x.sku === value.sku
                        );
                        guardarStockTotal(s ? s.stock : 0);
                      } else {
                        guardarMov({ ...mov, sku: null });
                        guardarStockTotal(null);
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Medicamento" size="small" />
                    )}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Fecha"
                    type="date"
                    size="small"
                    fullWidth
                    required
                    InputLabelProps={{ shrink: true }}
                    value={mov && mov.fecha ? mov.fecha : ""}
                    onChange={(e) => guardarMov({ ...mov, fecha: e.target.value })}
                  />
                </Grid>

                <Grid item xs={6}>
                  <TextField
                    label="Cantidad"
                    type="number"
                    size="small"
                    fullWidth
                    required
                    value={mov && mov.cantidad != null ? mov.cantidad : ""}
                    helperText={
                      stockTotal != null ? `Stock actual: ${stockTotal}` : " "
                    }
                    onChange={(e) => guardarMov({ ...mov, cantidad: e.target.value })}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={tipos}
                    disablePortal
                    value={tipos.find((t) => t.id === (mov && mov.tipo)) || null}
                    getOptionLabel={(o) => (o && o.nombre) || ""}
                    onChange={(e, value) => {
                      if (!value) {
                        guardarMov({ ...mov, tipo: "", almacen: "" });
                      } else if (value.id === "E") {
                        guardarMov({ ...mov, tipo: "E", almacen: 1 });
                      } else {
                        guardarMov({ ...mov, tipo: "S", almacen: "" });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Tipo de movimiento" size="small" />
                    )}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <Autocomplete
                    options={almacenes || []}
                    disablePortal
                    value={almacenValue}
                    disabled={Boolean(mov && mov.tipo === "E")}
                    getOptionDisabled={
                      mov && mov.tipo === "S"
                        ? (o) => o === (almacenes && almacenes[0])
                        : undefined
                    }
                    getOptionLabel={(o) => (o && o.nombre) || ""}
                    onChange={(e, value) => {
                      if (value) {
                        guardarMov({
                          ...mov,
                          almacen: value.id,
                          descripcion: value.nombre,
                        });
                      } else {
                        guardarMov({ ...mov, almacen: "", descripcion: "" });
                      }
                    }}
                    renderInput={(params) => (
                      <TextField {...params} label="Almacén" size="small" />
                    )}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Descripción"
                    size="small"
                    fullWidth
                    InputLabelProps={{ shrink: true }}
                    value={mov && mov.descripcion != null ? mov.descripcion : ""}
                    onChange={(e) => guardarMov({ ...mov, descripcion: e.target.value })}
                  />
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 2 }}>
              <Button onClick={onHide} color="inherit" startIcon={<CloseIcon />}>
                Cancelar
              </Button>
              <Button onClick={aceptarClick} variant="contained" startIcon={<CheckIcon />}>
                Aceptar
              </Button>
            </DialogActions>
          </Dialog>
        </ThemeProvider>
      ) : null}
    </Fragment>
  );
}
